import React, {useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {useDispatch, useSelector} from "react-redux";
import {HiArrowDown} from "react-icons/hi";
import {setProjectStats} from "../../../redux/slices/projects";
import {useChatService, useProjectService} from "../../../services";
import {useMentionFunctionality} from "../useMentionFunctionality";
import {useSSEChat} from "../useSSEChat";
import ChatLoadingSkeleton from "../ChatLoadingSkeleton";
import AtDropdown from "../AtDropdown";
import SearchChatInterface from "../SearchChatInterface";
import DelayedFadeIn from "../DelayedFadeIn";
import TypingGreeting from "../TypingGreeting";
import {detectEntityFromURL, buildResourcesEntityPayload} from "../utils";
import {Chat, HandleSubquestionsRequest, Message} from "../types";
import {MentionSearchType} from "../types/mentionSearch";
import MemoizedBotMessage from "../MemoizedBotMessage";
import MemoizedUserMessage from "../MemoizedUserMessage";
import {ENTITY} from "../../../redux/slices/timeline";
import {ChatPrefill, consumeChatPrefill} from "../prefill";
import GreetingPill from "./Greeting/GreetingPill";
import SourcesPanel from "./SourcesPanel";
import BigChatboxSubquestions from "./Subquestion/BigChatboxSubquestions";
import NormalChatboxSubquestions from "./Subquestion/NormalChatboxSubquestions";
import NewChatMentionTextArea from "./TextArea/NewChatMentionTextArea";
import ExistingChatMentionTextArea from "./TextArea/ExistingChatMentionTextArea";
import DateSeparator from "./DateSeperator";
import {setChatEntity} from "../../../redux/slices/chat";
import {getNextGreeting} from "../greetings";
import {useJwt} from "../../../helpers/common";

if (typeof window !== "undefined") {
  window._handleNewChatWithSSECleanup =
    window._handleNewChatWithSSECleanup || undefined;
}

interface ContextAwareChatInterfaceProps {
  isSharedChat?: boolean;
  readOnly?: boolean;
  onCloseSearchInterface: () => void;
  onSearchChatSelect: (chatId: number) => void;
}

const ContextAwareChatInterface: React.FC<ContextAwareChatInterfaceProps> = ({
  onCloseSearchInterface,
  onSearchChatSelect,
  isSharedChat = false,
  readOnly = false,
}) => {
  const jwtData: any = useJwt();
  const dispatch = useDispatch();
  const { handleChatSubquestions } = useChatService();
  const { fetchProjectStats } = useProjectService();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const atButtonRef = useRef<HTMLButtonElement>(null);
  const atButtonRefBottom = useRef<HTMLButtonElement>(null);
  const inputTextRef = useRef<HTMLTextAreaElement | null>(null);
  const lastEntityRef = useRef<string | null>(null);
  const { chatShareData } = useSelector((state: any) => state.chatMessages);
  const [currentGreeting, setCurrentGreeting] = useState("");
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [chatHistoryFetched, setChatHistoryFetched] = useState(false);
  const [subquestionsLoaded, setSubquestionsLoaded] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const [showAtDropdown, setShowAtDropdown] = useState(false);
  const [isDeveloperMode, setIsDeveloperMode] = useState(() => {
    return (
      window.location.pathname.includes("/v") ||
      new URLSearchParams(window.location.search).get("v") !== null
    );
  });
  const [resetExpansionCounter, setResetExpansionCounter] = useState(0);

  const {
    isLLMSettingsLoading
  } = useSelector((state: any) => state.settings);

  const {
    isSSEConnected,
    editMessage: sseEditMessage,
    respondWithEditedMessage,
    respondWithUserMessage,
    handleInterruptResponse,
    sendMessage: sseSendMessage,
    handleCancelMessage,
    cleanupOnChatChange,
  } = useSSEChat();

  const {
    inputText,
    setInputText,
    dropdownRef,
    suggestions,
    loadingSearch,
    loadingMore,
    showSuggestions,
    showDefaultOptions,
    selectedSuggestionIndex,
    defaultCategories,
    mentions,
    isDropdownFromButton,
    currentSearchType,
    currentSearchTerm,
    hasMoreAll,
    hasMoreEpics,
    hasMoreFeatures,
    hasMoreStrategyMaps,
    hasMoreTeams,
    hasMoreResources,
    loadMore,
    handleInputChange: handleMentionInputChange,
    handleKeyDown: handleMentionKeyDown,
    handleRoadmapSelectInternal,
    closeMentionDropdown,
    getTextWithContextTags,
    clearMentions,
    showMentionDropdownOnly,
    addMention,
    handleSearch,
  } = useMentionFunctionality(inputTextRef);
  const { currentEntity, currentEntityId, roadmapName, projectName } =
    useSelector((state: any) => state.timeline);

  const { data: projectsData, projectStatsLoading } = useSelector(
    (state: any) => state.projects
  );

  const {
    chats,
    isNewChat,
    contextInfo,
    showBigChatbox,
    isChatbotSidebarCollapsed,
    showSearchInterface,
    isCreatingSession,
    sessionId,
    lastActiveChatId,
    showChatbox,
    chatMessageByIdLoading,
    chatSubquestions,
    chatSubquestionsLoading,
    showSourcesPanel,
    chatEntity,
    profileId
  } = useSelector((state: any) => state.chatMessages);

  // Get resources page filter state
  const {
    capacityPlanningFilters,
    resourcePageSprints
  } = useSelector((state: any) => state.resources);
  const { teams: organizationTeams } = useSelector((state: any) => state.organization);

  const normalizeId = (value: unknown): string =>
    value == null ? '' : String(value).trim().toLowerCase();

  const scrollToUserMessage = useCallback(() => {
    if (
      messagesContainerRef.current &&
      currentChat?.messages &&
      currentChat?.messages.length > 0
    ) {
      const container = messagesContainerRef.current;

      // Apply smooth scroll behavior
      container.style.scrollBehavior = "smooth";

      // Wait for DOM to update
      requestAnimationFrame(() => {
        const userMessageElements =
          document.querySelectorAll<HTMLElement>(".user-message");

        if (userMessageElements.length > 0) {
          const lastUserMessageElement =
            userMessageElements[userMessageElements.length - 1];
          const containerHeight = container.clientHeight; // visible height (e.g., 450px)
          const lastUserMessageHeight = lastUserMessageElement.clientHeight;

          // Set dynamic bottom space = container height - user message height
          const dynamicSpaceHeight = Math.max(
            0,
            containerHeight - lastUserMessageHeight
          );

          // Update the dynamic space div first
          const existingSpaceDiv = container.querySelector<HTMLElement>(
            ".dynamic-bottom-space"
          );
          if (existingSpaceDiv) {
            existingSpaceDiv.style.height = `${dynamicSpaceHeight}px`;
          }

          // Wait for layout to update after changing dynamic space height
          setTimeout(() => {
            const messageOffsetTop = lastUserMessageElement.offsetTop;
            const targetScrollTop = Math.max(0, messageOffsetTop);

            // Set scroll position
            container.scrollTop = targetScrollTop;
          }, 50); // Small delay to ensure layout update
        }

        // Reset scroll behavior after animation
        setTimeout(() => {
          container.style.scrollBehavior = "auto";
        }, 300);
      });
    }
  }, [currentChat?.messages]);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const handleSendMessage = async () => {
    if(isLLMSettingsLoading) return;

    if (inputTextRef?.current) {
      inputTextRef.current.focus();
    }

    if (!inputText.trim()) return; // Prevent sending empty messages
    if (currentChat?.isStreaming && sessionId) return; // Prevent sending while streaming in this session

    closeMentionDropdown();

    const textWithContextTags = getTextWithContextTags();

    if (inputTextRef.current) {
      inputTextRef.current.style.height = "40px";
    }

    setInputText("");
    clearMentions();

    setTimeout(() => {
      scrollToUserMessage();
    }, 100);

    const lastBotMsg = currentChat?.messages
      ?.slice()
      .reverse()
      .find((m) => m.isBot);
    const hasQuestionInterrupt = !!(
      lastBotMsg &&
      Array.isArray(lastBotMsg.interrupts) &&
      lastBotMsg.interrupts.some((i: any) => i?.type === "question_ui")
    );
    const hasSimulationApprovalInterrupt = !!(
      lastBotMsg &&
      Array.isArray(lastBotMsg.interrupts) &&
      lastBotMsg.interrupts.some(
        (i: any) => i?.type === "simulation_approval_ui"
      )
    );

    setResetExpansionCounter(prev => prev + 1);

    if (hasQuestionInterrupt) {
      const qi = (lastBotMsg as any)?.interrupts.find(
        (i: any) => i?.type === "question_ui"
      );
      const interruptId = qi?.data?.interrupt_id;
      const chatId = currentChat?.chatId;
      const messageId = lastBotMsg?.messageId;
      await respondWithUserMessage("answer", textWithContextTags, {
        interruptId,
        chatId,
        messageId,
      });
    } else if (hasSimulationApprovalInterrupt) {
      const sai = (lastBotMsg as any)?.interrupts.find(
        (i: any) => i?.type === "simulation_approval_ui"
      );
      const interruptId = sai?.data?.interrupt_id;
      const chatId = currentChat?.chatId;
      const messageId = lastBotMsg?.messageId;
      await respondWithUserMessage("modify", textWithContextTags, {
        interruptId,
        chatId,
        messageId,
      });
    } else {
      await sseSendMessage(textWithContextTags);
    }

    return false;
  };

  const getCurrentContext = useCallback(() => {

    const currentPath = window.location.pathname;

    // FIRST: Check if we're on list pages - return null immediately to show dropdown
    if (
      currentPath === "/strategymaps" ||
      currentPath === "/workitems" ||
      currentPath === "/"
    ) {

      return null;
    }



    // SECOND: Check detail pages with URL matching
    // Strategy maps detail page - use timeline slice data
    if (
      currentPath.match(/\/strategymaps\/[^\/]+/) &&
      currentEntity === ENTITY.ROADMAP &&
      currentEntityId
    ) {
      return {
        type: "strategy_map", // Roadmaps are treated as epics in the mention system
        id: currentEntityId,
        name: roadmapName || `Strategy Map ${currentEntityId}`,
      };
    }

    // Project detail page - use entity/entityId from URL detection or detect directly from URL
    if (currentPath.match(/\/projects\/[^\/]+/)) {
      // Use existing state if available, otherwise detect from URL directly
      let projectEntityId = chatEntity?.entityId;
      if (
        !projectEntityId ||
        projectEntityId === "null" ||
        projectEntityId === "undefined"
      ) {
        const urlDetection = detectEntityFromURL();
        projectEntityId = urlDetection.entityId;
      }

      if (
        projectEntityId &&
        projectEntityId !== "null" &&
        projectEntityId !== "undefined"
      ) {
        // Try multiple sources for the project name (prioritize Redux data)
        const projectDisplayName =
          projectsData?.project_name ||
          contextInfo?.entity_name ||
          `Work Unit ${projectEntityId}`;

        return {
          type: "work_unit", // Will be converted to 'feature' in getTextWithContextTags
          id: projectEntityId,
          name: projectDisplayName,
        };
      }
    }


    // THIRD: Fallback for dashboard or other pages with timeline slice data
    if (currentEntity === ENTITY.ROADMAP && currentEntityId && roadmapName) {
      return {
        type: "strategy_map",
        id: currentEntityId,
        name: roadmapName,
      };
    }

    if (currentEntity === ENTITY.PROJECT && currentEntityId && projectName) {
      return {
        type: "work_unit", // Will be converted to 'feature' in getTextWithContextTags
        id: currentEntityId,
        name: projectName,
      };
    }

    // DEFAULT: return null to show dropdown
    return null;
  }, [
    currentEntity,
    currentEntityId,
    roadmapName,
    projectName,
    chatEntity?.entity,
    chatEntity?.entityId,
    contextInfo,
    projectsData,
  ]);

  const handleQuestionClick = (data: string) => {
    setInputText("");
    clearMentions();

    let processedText = data;
    let mentionSearchType = MentionSearchType.ALL;

    if (data.includes("<current_context>")) {
      const currentContext = getCurrentContext();

      if (currentContext) {
        const mentionText = `@${currentContext.name}`;
        processedText = data.replace("<current_context>", mentionText);

        const newMention = {
          id: currentContext.id,
          name: currentContext.name,
          type: currentContext.type,
          entity_id: currentContext.id,
          start: processedText.indexOf(mentionText),
          end: processedText.indexOf(mentionText) + mentionText.length,
          completed: true,
        };
        setTimeout(() => {
          addMention(newMention);
        }, 0);
      } else {
        processedText = data.replace("<current_context>", "@");
        setInputText(processedText);

        setTimeout(() => {
          if (inputTextRef.current) {
            const atIndex = processedText.indexOf("@");
            inputTextRef.current.setSelectionRange(atIndex + 1, atIndex + 1);

            const event = {
              target: {
                value: processedText,
                selectionStart: atIndex + 1,
              },
            };
            handleMentionInputChange(event);
          }
        }, 50);

        const textArea = inputTextRef.current;
        if (textArea) {
          setTimeout(() => {
            textArea.style.height = "inherit";
            const newHeight = Math.min(textArea.scrollHeight, 200);
            textArea.style.height = `${newHeight}px`;
            textArea.focus();
          }, 60);
        }
        return;
      }
    }

    const contextTagMatch = data.match(
      /<(work_unit|strategy_map|team|resource)_context>/
    );
    if (contextTagMatch) {
      const contextType = contextTagMatch[1];
      const currentContext = getCurrentContext();

      if (currentContext && currentContext.type === contextType) {
        const mentionText = `@${currentContext.name}`;
        processedText = data.replace(contextTagMatch[0], mentionText);

        const newMention = {
          id: currentContext.id,
          name: currentContext.name,
          type: currentContext.type,
          entity_id: currentContext.id,
          start: processedText.indexOf(mentionText),
          end: processedText.indexOf(mentionText) + mentionText.length,
          completed: true,
        };

        setTimeout(() => {
          addMention(newMention);
        }, 0);
      } else {
        if (contextType === "work_unit") {
          mentionSearchType = MentionSearchType.PROJECT;
        } else if (contextType === "strategy_map") {
          mentionSearchType = MentionSearchType.ROADMAP;
        } else if (contextType === "team") {
          mentionSearchType = MentionSearchType.TEAM;
        } else if (contextType === "resource") {
          mentionSearchType = MentionSearchType.RESOURCE;
        }

        processedText = data.replace(contextTagMatch[0], "@");
        setInputText(processedText);

        showMentionDropdownOnly(mentionSearchType);

        setTimeout(() => {
          if (inputTextRef.current) {
            const atIndex = processedText.indexOf("@");
            inputTextRef.current.setSelectionRange(atIndex + 1, atIndex + 1);

            const event = {
              target: {
                value: processedText,
                selectionStart: atIndex + 1,
              },
            };
            handleMentionInputChange(event);
          }
        }, 50);

        const textArea = inputTextRef.current;
        if (textArea) {
          setTimeout(() => {
            textArea.style.height = "inherit";
            const newHeight = Math.min(textArea.scrollHeight, 200);
            textArea.style.height = `${newHeight}px`;
            textArea.focus();
          }, 60);
        }
        return;
      }
    }

    setInputText(processedText);
    const textArea = inputTextRef.current;
    if (textArea) {
      setTimeout(() => {
        textArea.style.height = "inherit";
        const newHeight = Math.min(textArea.scrollHeight, 200);
        textArea.style.height = `${newHeight}px`;
        textArea.focus();
      }, 10);
    }
  };

  const handleQuestionRefresh = () => {
    const query = new URLSearchParams(window.location.search);
    const isnewproject = query.get("isnewproject") || "false";

    const entityPayload = buildResourcesEntityPayload(
      chatEntity?.entity,
      chatEntity?.entityId,
      contextInfo?.entity_name,
      capacityPlanningFilters,
      organizationTeams,
      resourcePageSprints
    );

    const payload: HandleSubquestionsRequest = {
      ...entityPayload,
      isnew: isnewproject === "true",
      is_first_time: false,
    };

    handleChatSubquestions(payload);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // First, let the mention functionality handle its keyboard events
    handleMentionKeyDown(e);

    // If mention functionality didn't handle the event, proceed with default behavior
    if (
      !(
        (showSuggestions && suggestions && suggestions.length > 0) ||
        (showDefaultOptions && defaultCategories.length > 0)
      )
    ) {
      // Default enter key behavior
      if (e.key === "Enter" && !e.shiftKey && !isCreatingSession) {
        e.preventDefault();
        if (currentChat?.isStreaming && sessionId) {
          // Don't send while streaming in this session
          return;
        }
        handleSendMessage();
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!isCreatingSession && !(currentChat?.isStreaming && sessionId)) {
      e.preventDefault();
      handleSendMessage();
    } else {
      e.preventDefault(); // Prevent form submission even when disabled
    }
  };

  const handleAtBtnClick = () => {
    // Toggle dropdown visibility
    if (showAtDropdown) {
      // Close dropdown if it's open
      closeMentionDropdown();
      setShowAtDropdown(false);
    } else {
      // Show the dropdown without adding @ to input
      showMentionDropdownOnly();
    }
    // Focus the input for better UX
    if (inputTextRef.current) {
      inputTextRef.current.focus();
    }
  };

  const canViewMemoryTags = useMemo(() => {
    const sharedActive = isSharedChat || Boolean(chatShareData);
    if (sharedActive) return false;

    const chatOwner = normalizeId(currentChat?.createdBy);
    const viewerId = normalizeId(profileId);

    if (!chatOwner || chatOwner === 'current_user') return true;
    return chatOwner === viewerId;
  }, [
    isSharedChat,
    chatShareData,
    currentChat?.createdBy,
    profileId,
  ]);

  const applyChatPrefill = useCallback(
    (prefill: ChatPrefill) => {
      if (!prefill || !inputTextRef.current) return;
      const textarea: any = inputTextRef.current;

      clearMentions();

      if (prefill.fullPrompt) {
        setInputText(prefill.fullPrompt);
        const mentionText = `@${prefill.name}`;
        const mentionStart = prefill.fullPrompt.indexOf(mentionText);

        if (mentionStart !== -1) {
          const newMention = {
            id: prefill.id,
            name: prefill.name,
            type: prefill.type,
            entity_id: prefill.id,
            start: mentionStart,
            end: mentionStart + mentionText.length,
            completed: true,
          } as any;
          try {
            addMention(newMention);
          } catch (_) {}
        }

        setTimeout(() => {
          if (prefill.fullPrompt) {
            textarea.setSelectionRange(
              prefill.fullPrompt.length,
              prefill.fullPrompt.length
            );
          }
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
        }, 0);
      } else {
        const mentionText = `@${prefill.name}`;
        setInputText(mentionText);
        const newMention = {
          id: prefill.id,
          name: prefill.name,
          type: prefill.type,
          entity_id: prefill.id,
          start: 0,
          end: mentionText.length,
          completed: true,
        } as any;
        try {
          addMention(newMention);
        } catch (_) {}

        setTimeout(() => {
          textarea.setSelectionRange(mentionText.length, mentionText.length);
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
        }, 0);
      }
    },
    [addMention, clearMentions, inputTextRef, setInputText]
  );

  const isNewDay = useCallback(
    (currentMessage: Message, previousMessage: Message | null) => {
      if (!previousMessage) return true;

      const currentTimestamp = currentMessage.modified_at;
      const previousTimestamp = previousMessage.modified_at;

      if (!currentTimestamp || !previousTimestamp) return false;

      const currentDate = new Date(
        currentTimestamp.includes("T") && !currentTimestamp.endsWith("Z")
          ? currentTimestamp + "Z"
          : currentTimestamp
      );
      const previousDate = new Date(
        previousTimestamp.includes("T") && !previousTimestamp.endsWith("Z")
          ? previousTimestamp + "Z"
          : previousTimestamp
      );

      return currentDate.toDateString() !== previousDate.toDateString();
    },
    []
  );

  const renderMessages = useMemo(() => {
    // Determine if this conversation spans more than one calendar day
    const hasMultipleDays = (() => {
      const messages = currentChat?.messages || [];
      const uniqueDays = new Set<string>();
      for (const m of messages) {
        const ts = m.modified_at;
        if (!ts) continue;
        const normalized =
          ts.includes("T") && !ts.endsWith("Z") ? ts + "Z" : ts;
        uniqueDays.add(new Date(normalized).toDateString());
        if (uniqueDays.size > 1) return true;
      }
      return false;
    })();

    // Find the index of the last bot message
    let lastBotMessageIndex = -1;
    if (currentChat?.messages && currentChat?.messages.length) {
      for (let i = currentChat.messages.length - 1; i >= 0; i--) {
        if (currentChat.messages[i].isBot) {
          lastBotMessageIndex = i;
          break;
        }
      }
    }

    // Find the index of the last user message
    let lastUserMessageIndex = -1;
    if (currentChat?.messages && currentChat?.messages.length) {
      for (let i = currentChat.messages.length - 1; i >= 0; i--) {
        if (!currentChat.messages[i].isBot) {
          lastUserMessageIndex = i;
          break;
        }
      }
    }

    return (
      currentChat?.messages &&
      currentChat?.messages.map((message, index) => {
        const key = message.id || `msg-${index}`;
        const messageWithKey = { ...message, stableKey: key };
        const previousMessage =
          index > 0 ? currentChat.messages[index - 1] : null;
        const showDateSeparator = isNewDay(message, previousMessage);

        const isLastBotMessage = message.isBot && index === lastBotMessageIndex;
        // Determine if the previous bot message (above this user message) has a blocking interrupt
        const prevBotHasBlockingInterrupt = (() => {
          if (message.isBot) return false;
          try {
            if (!currentChat?.messages || index <= 0) return false;
            for (let j = index - 1; j >= 0; j--) {
              const m: any = currentChat.messages[j];
              if (m?.isBot) {
                const interrupts = Array.isArray(m?.interrupts)
                  ? m.interrupts
                  : [];
                return interrupts.some(
                  (i: any) =>
                    i?.type === "question_ui" ||
                    i?.type === "simulation_approval_ui"
                );
              }
            }
          } catch {}
          return false;
        })();

        return (
          <React.Fragment key={key}>
            {hasMultipleDays && showDateSeparator && message.modified_at && (
              <DateSeparator timestamp={message.modified_at!} />
            )}

            {message.isBot ? (
              <MemoizedBotMessage
                key={key}
                index={index}
                message={message}
                currentChat={currentChat}
                inputTextRef={inputTextRef}
                isLastBotMessage={isLastBotMessage}
                scrollToUserMessage={scrollToUserMessage}
                canAddSelection={
                  !isSharedChat && !readOnly && !isCreatingSession
                }
                resetExpansion={resetExpansionCounter}
                isSSEConnected={isSSEConnected}
                respondWithUserMessage={respondWithUserMessage}
                handleInterruptResponse={handleInterruptResponse}
                sendMessage={sseSendMessage}
                canViewMemoryTags={canViewMemoryTags}
              />
            ) : (
              <MemoizedUserMessage
                key={key}
                message={messageWithKey}
                currentChat={currentChat}
                sseEditMessage={sseEditMessage}
                respondWithEditedMessage={respondWithEditedMessage}
                scrollToUserMessage={scrollToUserMessage}
                canEdit={
                  index === lastUserMessageIndex &&
                  !isSharedChat &&
                  !readOnly &&
                  !prevBotHasBlockingInterrupt
                }
                resetExpansion={resetExpansionCounter}
                onResetExpansion={() => setResetExpansionCounter(prev => prev + 1)}
              />
            )}
          </React.Fragment>
        );
      })
    );
  }, [currentChat?.messages, isNewDay]);

  useEffect(() => {
    if (showChatbox && isNewChat) {
      const useName = !!jwtData.first_name && Math.random() < 0.5;
      const personalizedGreeting = getNextGreeting(
        useName ? jwtData.first_name : ""
      );
      setCurrentGreeting(personalizedGreeting);
    }
  }, [showChatbox, isNewChat, jwtData?.first_name]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const isnewproject = query.get("isnewproject") || "false";
    setCurrentGreeting("");
    dispatch(setProjectStats({}));

    // Call APIs only once when chat modal is initially opened AND we have entity information
    // Skip initialization for shared/read-only chats
    if (
      showChatbox &&
      !chatHistoryFetched &&
      chatEntity?.entity &&
      !isSharedChat &&
      !readOnly
    ) {
      // Only fetch project stats if we're on a project page
      if (
        chatEntity?.entity === "project" &&
        chatEntity?.entityId &&
        chatEntity?.entityId !== "null" &&
        chatEntity?.entityId !== "undefined"
      ) {
        fetchProjectStats(chatEntity?.entityId, isnewproject);
      }

      // Call subquestions API for any page context only once
      if (!subquestionsLoaded) {
        const entityPayload = buildResourcesEntityPayload(
          chatEntity?.entity,
          chatEntity?.entityId,
          contextInfo?.entity_name,
          capacityPlanningFilters,
          organizationTeams,
          resourcePageSprints
        );

        const payload: HandleSubquestionsRequest = {
          ...entityPayload,
          isnew: isnewproject === "true",
          is_first_time: true,
        };
        handleChatSubquestions(payload);
        setSubquestionsLoaded(true);
      }

      setChatHistoryFetched(true);
    }

    // Reset flags when modal closes
    if (!showChatbox) {
      setChatHistoryFetched(false);
      setSubquestionsLoaded(false);
    }

    inputTextRef?.current?.focus();
  }, [showChatbox, chatEntity?.entity, chatEntity?.entityId]); // Add entity dependencies to wait for entity detection

  useEffect(() => {
    if (inputTextRef?.current) {
      inputTextRef.current.focus();
    }
  }, [lastActiveChatId]);

  useEffect(() => {
    if (!showChatbox && sessionId) {
      cleanupOnChatChange();
    }
  }, [showChatbox, sessionId, cleanupOnChatChange]);

  useEffect(() => {
    if (lastActiveChatId !== null) {
      setCurrentChat(chats[lastActiveChatId]);
    } else {
      setCurrentChat(null);
    }
  }, [lastActiveChatId, chats]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const dynamicBottomSpace = document.getElementsByClassName("dynamic-bottom-space")[0];
    if (!container || !dynamicBottomSpace) {
      return;
    }

    const handleScroll = () => {
      if (!container) {
        return;
      }

      if (isNewChat) {
        setShowScrollToBottom(false);
        return;
      }

      setShowScrollToBottom(container.scrollHeight > Math.round(container.scrollTop + (container.clientHeight + dynamicBottomSpace.clientHeight)));
    };

    handleScroll();

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [isNewChat, currentChat]);

  useEffect(() => {
    const checkDeveloperMode = () => {
      const devMode =
        window.location.pathname.includes("/v") ||
        new URLSearchParams(window.location.search).get("v") !== null;
      setIsDeveloperMode(devMode);
    };

    checkDeveloperMode();
    window.addEventListener("popstate", checkDeveloperMode);
    return () => window.removeEventListener("popstate", checkDeveloperMode);
  }, []);

  useEffect(() => {
    const shouldShow = showSuggestions || showDefaultOptions;
    setShowAtDropdown(shouldShow);
  }, [showSuggestions, showDefaultOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showAtDropdown) return;

      const target = event.target as Node;

      // Check if click is inside dropdown
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      // Check if click is inside input textarea
      if (inputTextRef.current && inputTextRef.current.contains(target)) {
        return;
      }

      // Check if click is on @ buttons
      if (
        (atButtonRef.current && atButtonRef.current.contains(target)) ||
        (atButtonRefBottom.current &&
          atButtonRefBottom.current.contains(target))
      ) {
        return; // Let the button handle its own toggle logic
      }

      // If we get here, the click was outside all relevant elements
      closeMentionDropdown();
      setShowAtDropdown(false);
    };

    if (showAtDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showAtDropdown, closeMentionDropdown]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showAtDropdown) {
        closeMentionDropdown();
        setShowAtDropdown(false);
        // Return focus to the input
        if (inputTextRef.current) {
          inputTextRef.current.focus();
        }
      }
    };

    if (showAtDropdown) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [showAtDropdown, closeMentionDropdown]);

  useEffect(() => {
    // Check if we're on dashboardnew with an ID - if so, use Redux state from contextInfo
    const isDashboardNewWithId = window.location.pathname.match(
      /\/strategymaps\/[^\/]+/
    );
    const isResources = window.location.pathname.includes("/resources");

    if ((isDashboardNewWithId || isResources) && contextInfo?.contextPayload) {
      // For dashboardnew/{id} or resources page, use entity information from Redux state (passed through contextInfo)
      // Entity is already mapped in GlobalChatBox.tsx (roadmap->strategymap, project->workunit)
      const entityFromContext = contextInfo.contextPayload.entity || "";
      const entityIdFromContext =
        contextInfo.contextPayload.entity_id || null;

      dispatch(
        setChatEntity({
          entity: entityFromContext,
          entityId: entityIdFromContext,
        })
      );
    } else {
      // For all other pages, use original URL-based detection
      const { entity, entityId } = detectEntityFromURL();

      dispatch(
        setChatEntity({
          entity: entity,
          entityId: entityId,
        })
      );
    }
  }, [contextInfo]);

  
  useEffect(() => {
    const prefill = consumeChatPrefill();
    if (prefill) applyChatPrefill(prefill);
  }, [applyChatPrefill]);

  useEffect(() => {
    const handler = (e: any) => {
      if (!e?.detail) return;
      applyChatPrefill(e.detail);
    };
    window.addEventListener("cadence:chat-prefill", handler);
    return () => window.removeEventListener("cadence:chat-prefill", handler);
  }, [applyChatPrefill]);

  // Refresh subquestions when entity changes during a new chat
  useEffect(() => {
    if (!isNewChat) return;

    const entity = chatEntity?.entity || "";
    const entityId = chatEntity?.entityId || "";
    if (!entity) return;

    const key = `${entity}:${entityId}`;
    // Initialize the last entity key without triggering a refresh
    if (lastEntityRef.current === null) {
      lastEntityRef.current = key;
      return;
    }
    if (lastEntityRef.current !== key) {
      lastEntityRef.current = key;
      // Use the existing question refresh function to avoid duplicating logic
      handleQuestionRefresh();
    }
  }, [chatEntity?.entity, chatEntity?.entityId, isNewChat]);

  if (showSearchInterface && showBigChatbox) {
    return (
      <SearchChatInterface
        onClose={onCloseSearchInterface}
        onChatSelect={onSearchChatSelect}
      />
    );
  }

  return (
    <div
      className={`flex gap-1 flex-col relative bg-white dark:bg-gray-900 h-full w-full`}
    >
      {currentChat?.isLoading &&
        !currentChat?.isStreaming &&
        !currentChat?.messages.length && (
          <div className="absolute top-0 left-0 right-0 z-10 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-2">
            <div className="flex items-center justify-center text-blue-700 dark:text-blue-300 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 dark:border-blue-300 mr-2" />
              Initializing chat session...
            </div>
          </div>
        )}

      {isDeveloperMode && (
        <div className="absolute top-2 right-2 z-20 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-3 py-1 rounded-full text-xs font-medium border border-orange-200 dark:border-orange-800 shadow-sm">
          🔧 Developer Mode
        </div>
      )}

      {chatMessageByIdLoading && (
        <ChatLoadingSkeleton
          showBigChatBox={showBigChatbox}
          sidebarCollapsed={isChatbotSidebarCollapsed}
        />
      )}

      {!chatMessageByIdLoading && (
        <div
          ref={messagesContainerRef}
          id="chat-container"
          className={`flex flex-col items-center justify-center overflow-auto overflow-x-hidden bg-white dark:bg-gray-900 h-full
            ${
              isSharedChat
                ? "w-full px-0 sm:px-[6%] md:px-[12%] lg:px-[20%]"
                : "w-full"
            }
            ${showSourcesPanel ? "md:mr-[380px]" : ""}`}
        >
          <div
            className={`h-full w-full 
              ${
                !isNewChat &&
                showBigChatbox &&
                isChatbotSidebarCollapsed &&
                !isSharedChat
                  ? "!px-8 !min-w-[60%] !max-w-[60%] !mx-auto"
                  : ""
              } 
              ${
                !isNewChat &&
                showBigChatbox &&
                !isChatbotSidebarCollapsed &&
                !isSharedChat
                  ? "!px-8 !min-w-[80%] !max-w-[80%] !mx-auto"
                  : ""
              } 
              ${
                !isNewChat && !showBigChatbox && !isSharedChat
                  ? "!w-full !px-8"
                  : ""
              }
              ${isSharedChat ? "!px-0 !py-4" : ""}
              ${readOnly && !isSharedChat ? "!px-8 !py-4" : ""}
            `}
          >
            {projectStatsLoading && (
              <div className="flex items-center flex-col gap-3 justify-between mb-2 w-[640px]">
                <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
                <div className="w-full h-6 bg-gray-300 rounded-md animate-pulse" />
              </div>
            )}

            {isNewChat && !isSharedChat && !readOnly && (
              <div
                className={`h-full flex flex-col items-center ${
                  showBigChatbox ? "justify-center" : "justify-end"
                }`}
              >
                <div
                  className={`flex flex-col items-center 
                    ${!showBigChatbox ? "justify-center flex-1" : "justify-end"}
                  `}
                >
                  <div
                    className={`relative ${
                      showBigChatbox
                        ? "flex flex-col md:flex-row items-center gap-3 sm:gap-4 md:gap-6"
                        : "flex flex-col items-center gap-4 sm:gap-6 md:gap-8"
                    } mb-6 sm:mb-8 md:mb-10`}
                  >
                    <DelayedFadeIn delay={300}>
                      <div className="group relative mx-auto w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 xl:w-18 xl:h-18 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center shadow-lg">
                        <img
                          src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                          alt="CadencePro Logo"
                          className="relative group-hover:animate-spin-slow z-10 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14"
                        />
                      </div>
                    </DelayedFadeIn>

                    {/* Greeting with Typing Animation */}
                    <div
                      className={`${
                        !showBigChatbox ? "text-center" : "flex items-center"
                      }`}
                    >
                      <TypingGreeting showBigChatBox={showBigChatbox} />
                    </div>

                    <div
                      className={`w-full absolute`}
                      style={{
                        top: showBigChatbox ? "-70%" : "-30%",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    >
                      <GreetingPill currentGreeting={currentGreeting} />
                    </div>
                  </div>

                  {!showBigChatbox &&
                    (chatSubquestionsLoading ||
                      (chatSubquestions && chatSubquestions.length > 0)) && (
                      <NormalChatboxSubquestions
                        onQuestionRefresh={handleQuestionRefresh}
                        onQuestionClick={handleQuestionClick}
                      />
                    )}
                </div>

                <NewChatMentionTextArea
                  currentChat={currentChat}
                  inputText={inputText}
                  inputTextRef={inputTextRef}
                  atButtonRef={atButtonRefBottom}
                  mentions={mentions}
                  onSubmit={handleSubmit}
                  onKeyDown={handleKeyDown}
                  onMentionInputChange={handleMentionInputChange}
                  onStopMessage={handleCancelMessage}
                  onAtBtnClick={handleAtBtnClick}
                />

                {showBigChatbox &&
                  (chatSubquestionsLoading ||
                    (chatSubquestions && chatSubquestions.length > 0)) && (
                    <BigChatboxSubquestions
                      onQuestionRefresh={handleQuestionRefresh}
                      onQuestionClick={handleQuestionClick}
                    />
                  )}
              </div>
            )}

            {renderMessages}

            {currentChat?.isLoading && !currentChat?.isStreaming ? (
              <div className="my message">
                <span className="jumping-dots">
                  <span className="dot-1"></span>
                  <span className="dot-2"></span>
                  <span className="dot-3"></span>
                </span>
              </div>
            ) : null}

            {currentChat?.messages && currentChat?.messages.length > 1 && (
              <div
                className="dynamic-bottom-space"
                style={{
                  height: messagesContainerRef.current
                    ? (() => {
                        const container = messagesContainerRef.current;
                        const userMessages =
                          document.querySelectorAll(".user-message");
                        const botMessages =
                          document.querySelectorAll(".bot-message");
                        const lastMessage = Array.isArray(currentChat?.messages)
                          ? currentChat.messages.at(-1)
                          : null;
                        const isLastBotMessageAndHasText =
                          lastMessage?.isBot && lastMessage?.text;

                        if (
                          userMessages.length > 0 &&
                          botMessages.length > 0 &&
                          isLastBotMessageAndHasText
                        ) {
                          const lastUserMessage =
                            userMessages[userMessages.length - 1];
                          const lastBotMessage =
                            botMessages[botMessages.length - 1];

                          const containerHeight = container.clientHeight;
                          const userMessageHeight =
                            lastUserMessage.clientHeight;
                          const botMessageHeight = lastBotMessage.clientHeight;

                          return Math.max(
                            containerHeight -
                              userMessageHeight -
                              botMessageHeight,
                            0
                          );
                        }
                      })()
                    : "",
                }}
              />
            )}
          </div>
        </div>
      )}

      {!isNewChat && !isSharedChat && !readOnly && (
        <div className="relative">
          {showScrollToBottom && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="absolute left-1/2 transform -translate-x-1/2 -top-9 z-10 h-7 w-7 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 shadow-sm flex items-center justify-center transition-all hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500"
              aria-label="Scroll to latest message"
            >
              <HiArrowDown className="h-3.5 w-3.5" />
            </button>
          )}

          <ExistingChatMentionTextArea
            currentChat={currentChat}
            inputText={inputText}
            inputTextRef={inputTextRef}
            atButtonRef={atButtonRef}
            mentions={mentions}
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            onMentionInputChange={handleMentionInputChange}
            onStopMessage={handleCancelMessage}
            onAtBtnClick={handleAtBtnClick}
          />
        </div>
      )}

      <AtDropdown
        key={`chat-${lastActiveChatId ?? currentChat?.chatId ?? 'new'}`}
        showDropdown={showAtDropdown}
        selectedSuggestionIndex={selectedSuggestionIndex}
        suggestions={suggestions}
        loadingSearch={loadingSearch}
        loadingMore={loadingMore}
        handleRoadmapSelectInternal={handleRoadmapSelectInternal}
        dropdownRef={dropdownRef}
        isDropdownFromButton={isDropdownFromButton}
        textareaRef={inputTextRef}
        hasMoreAll={hasMoreAll}
        hasMoreEpics={hasMoreEpics}
        hasMoreFeatures={hasMoreFeatures}
        hasMoreStrategyMaps={hasMoreStrategyMaps}
        hasMoreTeams={hasMoreTeams}
        hasMoreResources={hasMoreResources}
        onLoadMore={loadMore}
        onBackendSearch={handleSearch}
        initialSearchType={currentSearchType}
        currentSearchTerm={currentSearchTerm}
      />

      {showSourcesPanel && <SourcesPanel />}
    </div>
  );
};

export default ContextAwareChatInterface;